import { useEffect, useState } from 'react';
import { fetchTimeSeries } from '../services/statisticsService';
import type { StatsGranularity, StatsMetric, TimeSeriesResponse } from '../services/types';

/** Zustand einer geladenen Statistik-Zeitreihe. */
export interface TimeSeriesState {
  /** Geladene Zeitreihe oder null (noch nicht geladen bzw. Fehler). */
  readonly data: TimeSeriesResponse | null;
  /** true, waehrend ein Request laeuft. */
  readonly loading: boolean;
  /** Deutsche Fehlermeldung oder null, wenn kein Fehler vorliegt. */
  readonly error: string | null;
}

/** Fehlermeldung fuer fehlgeschlagene Zeitreihen-Requests. */
const LOAD_ERROR_MESSAGE = 'Die Statistikdaten konnten nicht geladen werden.';

/**
 * Geladenes Ergebnis samt Datenschluessel. Der Schluessel identifiziert die
 * Parameterkombination, zu der die Daten gehoeren.
 */
interface LoadedTimeSeries {
  readonly key: string;
  readonly data: TimeSeriesResponse | null;
  readonly error: string | null;
}

/** Eindeutiger Schluessel einer Parameterkombination. */
function buildRequestKey(
  metric: StatsMetric,
  granularity: StatsGranularity,
  from?: string,
  to?: string
): string {
  return `${metric}|${granularity}|${from ?? ''}|${to ?? ''}`;
}

/**
 * Laedt eine Statistik-Zeitreihe und laedt bei jeder Aenderung von
 * granularity/from/to neu (kein Caching, Projektstandard).
 *
 * from/to undefined bedeutet: Parameter weglassen, Server-Defaults nutzen.
 *
 * Kein Stale-Data-Fenster: Zum geladenen Ergebnis wird der Datenschluessel
 * mitgefuehrt. Passen die aktuellen Props nicht dazu (Parameterwechsel,
 * Request noch unterwegs), meldet der Hook `loading` und liefert KEINE
 * Daten — sonst wuerden alte Punkte kurzzeitig unter neuer Granularitaet
 * formatiert (z. B. Tagesdaten als Kalenderwochen).
 *
 * Fehler werden bewusst still behandelt (nur Fehlerzustand, KEIN
 * console.error): Die aufrufende Card rendert den Fehlerzustand;
 * zusaetzliche Console-Errors wuerden u. a. die E2E-Error-Collector-Fixture
 * ausloesen.
 */
export function useTimeSeries(
  metric: StatsMetric,
  granularity: StatsGranularity,
  from?: string,
  to?: string
): TimeSeriesState {
  const requestKey = buildRequestKey(metric, granularity, from, to);
  const [loaded, setLoaded] = useState<LoadedTimeSeries | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchTimeSeries(metric, { granularity, from, to }, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) {
          // Abbruch durch Parameterwechsel oder Unmount: Ergebnis verwerfen
          return;
        }
        setLoaded({ key: requestKey, data: response, error: null });
      })
      .catch(() => {
        if (controller.signal.aborted) {
          // Abbruch durch Parameterwechsel oder Unmount: kein Fehlerzustand
          return;
        }
        setLoaded({ key: requestKey, data: null, error: LOAD_ERROR_MESSAGE });
      });
    return () => {
      controller.abort();
    };
  }, [metric, granularity, from, to, requestKey]);

  const isCurrent = loaded !== null && loaded.key === requestKey;
  return {
    data: isCurrent ? loaded.data : null,
    loading: !isCurrent,
    error: isCurrent ? loaded.error : null,
  };
}

export default useTimeSeries;
